import {
  type CertificadoAcessosArbitral,
  type CertificadoGerado,
  mapCertificadoAcessos,
} from '@/features/arbitral-detalhes';
import { certificadoAcessosDtoSchema } from '@/shared/validation/certificadoAcessosDtoSchemas';
import { throwApiError } from '@/shared/utils/apiResponse';
import { apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import type { ProcessoArbitro, ProcessoArbitroSeed } from '../types';
import type { ModelosEmissaoPagina, ModelosEmissaoResponse } from '../types/modelosEmissaoTypes';
import type {
  BaixarCertificadoInput,
  BaixarDocumentoInput,
  ConclusoRespostaContexto,
  CriarAndamentoInput,
  EmissaoCategoria,
  EmissaoModeloPreview,
  EmissaoRascunho,
  ObterProcessoResult,
  PainelAndamento,
  PainelDocumento,
  PainelDashboardResponse,
  PainelFinanceiroResponse,
  PainelListQuery,
  PainelListResponse,
  PainelNotificacaoFeed,
  PainelPendencia,
  PainelProcessoItem,
  PublicarDocumentoInput,
  SalvarRascunhoInput,
  DadosContratoConferidos,
} from '../types/painelListTypes';
import { enriquecerProcesso } from '../utils/detalhe';
import type { ConsultarRespostaInput, ReciboRespostaPendencia } from '../types/reciboRespostaTypes';

const buildPainelQuery = (query: PainelListQuery): string => {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.status && query.status.length > 0) params.set('status', query.status.join(','));
  if (query.situacaoId && query.situacaoId.length > 0)
    params.set('situacaoId', query.situacaoId.join(','));
  params.set('page', String(query.page));
  params.set('limit', String(query.limit));
  params.set('sortBy', query.sortBy);
  params.set('sortOrder', query.sortOrder);
  return params.toString();
};

export const painelProcessosService = {
  listar: async (query: PainelListQuery): Promise<PainelListResponse> => {
    const response = await apiGet(`/arbitral/painel/processos?${buildPainelQuery(query)}`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao listar processos');
    }
    const result = await response.json();
    return result.data as PainelListResponse;
  },

  obter: async (id: string): Promise<ObterProcessoResult> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}`);
    if (response.status === 404) {
      return { kind: 'not_found' };
    }
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar processo');
    }
    const result = await response.json();
    return { kind: 'ok', detalhe: result.data };
  },

  listarAndamentos: async (id: string): Promise<PainelAndamento[]> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}/andamentos`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar andamentos');
    }
    const result = await response.json();
    return result.data as PainelAndamento[];
  },

  criarAndamento: async ({
    id,
    descricao,
    descricaoHtml,
    arquivos,
    pendenciaId,
    clientRequestId,
  }: CriarAndamentoInput): Promise<ReciboRespostaPendencia | null> => {
    const form = new FormData();
    form.append('descricao', descricao);
    if (descricaoHtml) form.append('descricaoHtml', descricaoHtml);
    if (pendenciaId) form.append('pendenciaId', pendenciaId);
    if (clientRequestId) form.append('clientRequestId', clientRequestId);
    arquivos.forEach((arquivo) => form.append('arquivos', arquivo, arquivo.name));
    const response = await apiPost(`/arbitral/painel/processos/${id}/andamentos`, form);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao registrar andamento');
    }
    if (!clientRequestId) return null;
    const result = await response.json();
    return result.data as ReciboRespostaPendencia | null;
  },

  consultarResposta: async ({
    id,
    pendenciaId,
    clientRequestId,
  }: ConsultarRespostaInput): Promise<ReciboRespostaPendencia | null> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/pendencias/${pendenciaId}/respostas/${clientRequestId}`,
    );
    if (!response.ok) await throwApiError(response, 'Não foi possível conferir a resposta');
    const result = await response.json();
    return result.data as ReciboRespostaPendencia | null;
  },

  listarDocumentos: async (id: string): Promise<PainelDocumento[]> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}/documentos`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar documentos');
    }
    const result = await response.json();
    return result.data as PainelDocumento[];
  },

  // Mesmo registro canônico que Secretaria/Requerente/Requerido já consultam (regra de
  // consistência do ClickUp 86ak8ku9c) — só a porta muda (`/painel/...`, gate por nomeação).
  listarCertificados: async (id: string): Promise<CertificadoGerado[]> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}/certificados`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar os certificados');
    }
    const result = await response.json();
    return result.data as CertificadoGerado[];
  },

  baixarCertificado: async ({ id, certificadoId }: BaixarCertificadoInput): Promise<string> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/certificados/${certificadoId}/download`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao baixar certificado');
    }
    const result = await response.json();
    return result.data.url as string;
  },

  obterCertificadoAcessos: async (id: string): Promise<CertificadoAcessosArbitral> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}/certificado-acessos`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar o Certificado de Acessos');
    }
    const body = await response.json();
    const dto = certificadoAcessosDtoSchema.parse(body?.data ?? body);
    return mapCertificadoAcessos(dto);
  },

  emitirTermoImparcialidade: async (id: string): Promise<void> => {
    const response = await apiPost(`/arbitral/painel/processos/${id}/termo-imparcialidade`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao emitir o termo de imparcialidade');
    }
  },

  // Motivo opcional (art. 27, § 3º): sem ele vai `{}`, e o core grava o motivo nulo. Nunca vai
  // para o andamento nem para a telemetria.
  recusarNomeacao: async (nomeacaoId: string, motivo: string | null): Promise<void> => {
    const response = await apiPost(
      `/arbitral/nomeacoes/${encodeURIComponent(nomeacaoId)}/recusa`,
      motivo ? { motivo } : {},
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao recusar a nomeação');
    }
  },

  listarPendencias: async (id: string): Promise<PainelPendencia[]> => {
    const response = await apiGet(`/arbitral/painel/processos/${id}/pendencias`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar pendências');
    }
    const result = await response.json();
    return result.data as PainelPendencia[];
  },

  obterDashboard: async (): Promise<PainelDashboardResponse> => {
    const response = await apiGet('/arbitral/painel/dashboard');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar o painel');
    }
    const result = await response.json();
    return result.data as PainelDashboardResponse;
  },

  obterFinanceiro: async (): Promise<PainelFinanceiroResponse> => {
    const response = await apiGet('/arbitral/painel/financeiro');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar o financeiro');
    }
    const result = await response.json();
    return result.data as PainelFinanceiroResponse;
  },

  listarNotificacoes: async (): Promise<PainelNotificacaoFeed> => {
    const response = await apiGet('/arbitral/painel/notificacoes');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar notificações');
    }
    const result = await response.json();
    return result.data as PainelNotificacaoFeed;
  },

  marcarAvisoLido: async (recipienteId: string): Promise<void> => {
    const response = await apiPost(`/arbitral/painel/notificacoes/avisos/${recipienteId}/lida`);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao marcar aviso');
    }
  },

  marcarAvisosLidos: async (): Promise<void> => {
    const response = await apiPost('/arbitral/painel/notificacoes/avisos/lidas');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao marcar avisos');
    }
  },

  obterContextoConcluso: async (
    id: string,
    pendenciaId: string,
  ): Promise<ConclusoRespostaContexto | null> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/pendencias/${pendenciaId}/concluso`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar o concluso');
    }
    const result = await response.json();
    return (result.data as ConclusoRespostaContexto | null) ?? null;
  },

  listarTermosPendentes: async (): Promise<PainelProcessoItem[]> => {
    const response = await apiGet('/arbitral/painel/processos/termos-imparcialidade/pendentes');
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar termos pendentes');
    }
    const result = await response.json();
    return result.data as PainelProcessoItem[];
  },

  emitirTermosImparcialidadeLote: async (ids: string[]): Promise<void> => {
    const response = await apiPost('/arbitral/painel/processos/termos-imparcialidade', {
      processoIds: ids,
    });
    if (!response.ok) {
      await throwApiError(response, 'Erro ao emitir os termos em lote');
    }
  },

  baixarDocumento: async ({ id, documentoId }: BaixarDocumentoInput): Promise<string> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/documentos/${documentoId}/download`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao abrir documento');
    }
    const result = await response.json();
    return result.data.url as string;
  },

  listarModelosEmissao: async (
    id: string,
    categoria: EmissaoCategoria,
    page = 1,
  ): Promise<ModelosEmissaoPagina> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/emissao/modelos?categoria=${categoria}&page=${page}&limit=20`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar modelos');
    }
    const result: ModelosEmissaoResponse = await response.json();
    // Um rollback do core pode devolver o contrato antigo: mantém a primeira página utilizável.
    return {
      items: result.data,
      pagination: result.pagination ?? {
        page,
        limit: 20,
        total: result.data.length,
        totalPages: page,
      },
    };
  },

  previewModeloEmissao: async (
    id: string,
    templateVersaoId: string,
    dadosContrato?: DadosContratoConferidos,
  ): Promise<EmissaoModeloPreview> => {
    const query = new URLSearchParams();
    for (const [campo, valor] of Object.entries(dadosContrato ?? {})) {
      if (valor?.trim()) query.set(campo, valor.trim());
    }
    const sufixo = query.toString();
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/emissao/modelos/${templateVersaoId}/preview${sufixo ? `?${sufixo}` : ''}`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar o modelo');
    }
    const result = await response.json();
    return result.data as EmissaoModeloPreview;
  },

  obterRascunhoEmissao: async (
    id: string,
    categoria: EmissaoCategoria,
  ): Promise<EmissaoRascunho | null> => {
    const response = await apiGet(
      `/arbitral/painel/processos/${id}/emissao/rascunho?categoria=${categoria}`,
    );
    if (!response.ok) {
      await throwApiError(response, 'Erro ao carregar rascunho');
    }
    const result = await response.json();
    return result.data as EmissaoRascunho | null;
  },

  salvarRascunhoEmissao: async (
    id: string,
    body: SalvarRascunhoInput,
  ): Promise<EmissaoRascunho> => {
    const response = await apiPut(`/arbitral/painel/processos/${id}/emissao/rascunho`, body);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao salvar rascunho');
    }
    const result = await response.json();
    return result.data as EmissaoRascunho;
  },

  publicarDocumentoEmissao: async (id: string, body: PublicarDocumentoInput): Promise<void> => {
    const response = await apiPost(`/arbitral/painel/processos/${id}/emissao/publicar`, body);
    if (!response.ok) {
      await throwApiError(response, 'Erro ao publicar o documento');
    }
  },
};

const MOCK_LATENCY_MS = 280;

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), MOCK_LATENCY_MS));

const processosSeed: ProcessoArbitroSeed[] = [
  {
    id: 'p1',
    numero: '0001428-72.2026.8.26.0100',
    tipo: 'despejo_fp',
    requerente: { nome: 'Imobiliária Costa Verde Ltda.', documento: '12.448.901/0001-55' },
    requerido: { nome: 'Marcos Aurélio Tavares', documento: '284.119.770-08' },
    requeridos: [
      {
        nome: 'Marcos Aurélio Tavares',
        documento: '284.119.770-08',
        advogado: { nome: 'Dr. Paulo Vieira', oab: 'SP 176.402' },
      },
      { nome: 'Fernanda Tavares Ribeiro', documento: '351.882.640-19' },
    ],
    status: 'em_citacao',
    situacao: 'citacao_parcial',
    termoImparcialidade: false,
    termoArbitragem: false,
    novo: true,
    ultimaMovimentacao: '2026-05-31',
    atribuidoEm: '2026-05-31',
    contrato: {
      numero: 'LOC-2024-0912',
      clausulaArbitral: 'Cláusula 18ª',
      clausulaCompromissoria: 'Cláusula 21ª',
      valor: 'R$ 18.600,00',
    },
    pendencias: [],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p2',
    numero: '0001391-04.2026.8.26.0100',
    tipo: 'rescisao',
    requerente: { nome: 'Northbridge Participações S.A.', documento: '29.770.118/0001-42' },
    requerentes: [
      { nome: 'Northbridge Participações S.A.', documento: '29.770.118/0001-42' },
      { nome: 'Northbridge Fundo de Investimento', documento: '33.410.552/0001-70' },
    ],
    requerido: { nome: 'Lúcia Helena Moraes ME', documento: '41.882.330/0001-19' },
    status: 'em_andamento',
    situacao: 'aguardando_termo_arbitragem',
    termoImparcialidade: true,
    termoArbitragem: false,
    novo: false,
    ultimaMovimentacao: '2026-05-29',
    atribuidoEm: '2026-05-25',
    contrato: {
      numero: 'PRC-2023-1180',
      clausulaArbitral: 'Cláusula 12ª',
      clausulaCompromissoria: 'Cláusula 15ª',
      valor: 'R$ 142.500,00',
    },
    pendencias: [
      {
        id: 'pd2',
        tipo: 'aguardando',
        descricao: 'Aguardando Termo de Arbitragem da secretaria',
        prazo: '2026-06-05',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p3',
    numero: '0001205-88.2026.8.26.0100',
    tipo: 'despejo_alug',
    requerente: { nome: 'Condomínio Edifício Lumière', documento: '08.221.554/0001-70' },
    requerido: { nome: 'Bistrô da Esquina Ltda.', documento: '33.901.776/0001-88' },
    status: 'em_andamento',
    situacao: 'concluso_recebido',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-30',
    atribuidoEm: '2026-05-12',
    contrato: {
      numero: 'LOC-2022-0455',
      clausulaArbitral: 'Cláusula 9ª',
      clausulaCompromissoria: 'Cláusula 14ª',
      valor: 'R$ 96.300,00',
    },
    pendencias: [
      {
        id: 'pd3',
        tipo: 'concluso',
        descricao: 'Concluso: comprovante de pagamento juntado pela parte requerida',
        prazo: '2026-06-03',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p4',
    numero: '0001098-31.2026.8.26.0100',
    tipo: 'rescisao',
    requerente: {
      nome: 'Atlântico Fomento Mercantil S.A.',
      documento: '17.554.220/0001-30',
      advogado: { nome: 'Dra. Fábia Menezes', oab: 'SP 198.220' },
    },
    requerido: {
      nome: 'Pedro Henrique Vasconcelos',
      documento: '118.330.945-72',
      advogado: { nome: 'Dr. Ronaldo Lopes', oab: 'SP 245.881' },
    },
    status: 'em_andamento',
    situacao: 'aguardando_sentenca',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-27',
    atribuidoEm: '2026-04-28',
    contrato: {
      numero: 'PRC-2023-0741',
      clausulaArbitral: 'Cláusula 11ª',
      clausulaCompromissoria: 'Cláusula 16ª',
      valor: 'R$ 73.900,00',
    },
    pendencias: [
      {
        id: 'pd4',
        tipo: 'sentenca',
        descricao: 'Processo apto para sentença — decurso de prazo sem manifestação',
        prazo: '2026-06-04',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p5',
    numero: '0000984-15.2026.8.26.0100',
    tipo: 'despejo_fp',
    requerente: { nome: 'Patrimonial Andradas Ltda.', documento: '22.118.700/0001-44' },
    requerido: { nome: 'Camila Souza Restaurante ME', documento: '39.220.115/0001-67' },
    requeridos: [
      { nome: 'Camila Souza Restaurante ME', documento: '39.220.115/0001-67' },
      { nome: 'Camila Souza Ferreira', documento: '288.140.905-33' },
      { nome: 'Rodrigo Souza Ferreira', documento: '371.559.028-14' },
      { nome: 'Marta Aparecida Souza', documento: '419.007.662-80' },
    ],
    status: 'em_andamento',
    situacao: 'aguardando_sentenca',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-24',
    atribuidoEm: '2026-04-20',
    contrato: {
      numero: 'LOC-2023-0322',
      clausulaArbitral: 'Cláusula 10ª',
      clausulaCompromissoria: 'Cláusula 13ª',
      valor: 'R$ 31.200,00',
    },
    pendencias: [
      {
        id: 'pd5',
        tipo: 'sentenca',
        descricao: 'Processo apto para sentença',
        prazo: '2026-06-02',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p6',
    numero: '0000871-59.2026.8.26.0100',
    tipo: 'despejo_alug',
    requerente: { nome: 'Holding Sete Quedas Ltda.', documento: '44.012.778/0001-05' },
    requerido: { nome: 'Oficina Mecânica Veloz ME', documento: '28.554.110/0001-93' },
    status: 'sentenca_emitida',
    situacao: 'aguardando_recebimento',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-26',
    atribuidoEm: '2026-03-30',
    contrato: {
      numero: 'LOC-2022-0188',
      clausulaArbitral: 'Cláusula 8ª',
      clausulaCompromissoria: 'Cláusula 12ª',
      valor: 'R$ 58.700,00',
    },
    pendencias: [
      {
        id: 'pd6',
        tipo: 'prazo',
        descricao: 'Aguardando decurso do prazo de embargos de declaração',
        prazo: '2026-06-10',
      },
    ],
    dataSentenca: '2026-05-26',
    prazoEmbargos: '2026-06-10',
  },
  {
    id: 'p7',
    numero: '0000742-03.2026.8.26.0100',
    tipo: 'rescisao',
    requerente: { nome: 'Vale do Sol Agronegócio S.A.', documento: '51.220.448/0001-12' },
    requerido: { nome: 'Distribuidora Aurora Ltda.', documento: '37.118.005/0001-29' },
    status: 'sentenca_emitida',
    situacao: 'embargos_opostos',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-14',
    atribuidoEm: '2026-03-10',
    contrato: {
      numero: 'PRC-2022-0530',
      clausulaArbitral: 'Cláusula 14ª',
      clausulaCompromissoria: 'Cláusula 17ª',
      valor: 'R$ 210.400,00',
    },
    pendencias: [
      {
        id: 'pd7',
        tipo: 'embargos',
        descricao: 'Embargos de declaração opostos pela parte requerida — pendentes de resposta',
        prazo: '2026-06-01',
      },
    ],
    dataSentenca: '2026-05-14',
    prazoEmbargos: '2026-06-01',
  },
  {
    id: 'p8',
    numero: '0000615-77.2026.8.26.0100',
    tipo: 'cobranca',
    requerente: { nome: 'Banco Meridiano S.A.', documento: '60.118.220/0001-77' },
    requerido: { nome: 'Eduardo Lima Confecções ME', documento: '29.880.114/0001-30' },
    status: 'concluido',
    situacao: 'transito_julgado',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-20',
    atribuidoEm: '2026-02-15',
    contrato: {
      numero: 'PRC-2021-0922',
      clausulaArbitral: 'Cláusula 7ª',
      clausulaCompromissoria: 'Cláusula 11ª',
      valor: 'R$ 44.100,00',
    },
    pendencias: [],
    dataSentenca: '2026-05-02',
    prazoEmbargos: '2026-05-17',
  },
  {
    id: 'p9',
    numero: '0001440-19.2026.8.26.0100',
    tipo: 'cobranca',
    requerente: { nome: 'Fintech Crédito Já S.A.', documento: '48.220.117/0001-58' },
    requerido: { nome: 'Roberta Nunes Magazine ME', documento: '31.114.880/0001-46' },
    status: 'em_citacao',
    situacao: 'arbitro_nomeado',
    termoImparcialidade: false,
    termoArbitragem: false,
    novo: true,
    ultimaMovimentacao: '2026-06-01',
    atribuidoEm: '2026-06-01',
    contrato: {
      numero: 'PRC-2024-1402',
      clausulaArbitral: 'Cláusula 13ª',
      clausulaCompromissoria: 'Cláusula 16ª',
      valor: 'R$ 27.300,00',
    },
    pendencias: [],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p10',
    numero: '0001312-66.2026.8.26.0100',
    tipo: 'despejo_fp',
    requerente: { nome: 'São Bento Imóveis Ltda.', documento: '19.770.554/0001-21' },
    requerido: { nome: 'Padaria Pão Quente ME', documento: '33.118.220/0001-90' },
    requeridos: [
      { nome: 'Padaria Pão Quente ME', documento: '33.118.220/0001-90' },
      { nome: 'Sebastião Nunes de Almeida', documento: '405.223.118-70' },
      { nome: 'Cláudia Nunes de Almeida', documento: '512.884.330-19' },
    ],
    status: 'em_andamento',
    situacao: 'aguardando_despacho',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-22',
    atribuidoEm: '2026-05-01',
    contrato: {
      numero: 'LOC-2023-0611',
      clausulaArbitral: 'Cláusula 9ª',
      clausulaCompromissoria: 'Cláusula 13ª',
      valor: 'R$ 22.900,00',
    },
    pendencias: [],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p11',
    numero: '0001260-90.2026.8.26.0100',
    tipo: 'rescisao',
    requerente: { nome: 'Construtora Horizonte Ltda.', documento: '08.451.220/0001-90' },
    requerido: { nome: 'Engenharia Prumo S.A.', documento: '52.118.770/0001-14' },
    status: 'em_andamento',
    situacao: 'aguardando_termo_arbitragem',
    termoImparcialidade: true,
    termoArbitragem: false,
    novo: false,
    ultimaMovimentacao: '2026-05-28',
    atribuidoEm: '2026-05-20',
    contrato: {
      numero: 'PRC-2024-0210',
      clausulaArbitral: 'Cláusula 15ª',
      clausulaCompromissoria: 'Cláusula 19ª',
      valor: 'R$ 318.700,00',
    },
    pendencias: [
      {
        id: 'pd11',
        tipo: 'aguardando',
        descricao: 'Aguardando Termo de Arbitragem da secretaria',
        prazo: '2026-06-06',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p12',
    numero: '0001155-42.2026.8.26.0100',
    tipo: 'cobranca',
    requerente: { nome: 'Cooperativa de Crédito União', documento: '11.220.448/0001-66' },
    requerido: { nome: 'Auto Peças Central Ltda.', documento: '29.770.118/0001-83' },
    status: 'em_andamento',
    situacao: 'concluso_recebido',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-19',
    atribuidoEm: '2026-04-15',
    contrato: {
      numero: 'PRC-2023-0855',
      clausulaArbitral: 'Cláusula 12ª',
      clausulaCompromissoria: 'Cláusula 15ª',
      valor: 'R$ 67.500,00',
    },
    pendencias: [
      {
        id: 'pd12',
        tipo: 'concluso',
        descricao: 'Concluso: pedido de parcelamento apresentado pela parte requerida',
        prazo: '2026-06-07',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p13',
    numero: '0001020-77.2026.8.26.0100',
    tipo: 'despejo_alug',
    requerente: { nome: 'Edifício Comercial Paulista', documento: '07.118.554/0001-40' },
    requerido: { nome: 'Studio Beleza & Cia ME', documento: '38.220.117/0001-52' },
    status: 'em_andamento',
    situacao: 'aguardando_sentenca',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-31',
    atribuidoEm: '2026-04-10',
    contrato: {
      numero: 'LOC-2023-0277',
      clausulaArbitral: 'Cláusula 10ª',
      clausulaCompromissoria: 'Cláusula 14ª',
      valor: 'R$ 39.800,00',
    },
    pendencias: [
      {
        id: 'pd13',
        tipo: 'sentenca',
        descricao: 'Processo apto para sentença',
        prazo: '2026-06-08',
      },
    ],
    dataSentenca: null,
    prazoEmbargos: null,
  },
  {
    id: 'p14',
    numero: '0000930-08.2026.8.26.0100',
    tipo: 'rescisao',
    requerente: { nome: 'Logística Trans Brasil S.A.', documento: '55.118.220/0001-90' },
    requerido: { nome: 'Mercado Bom Preço Ltda.', documento: '34.770.115/0001-28' },
    status: 'em_andamento',
    situacao: 'decurso_prazo',
    termoImparcialidade: true,
    termoArbitragem: true,
    novo: false,
    ultimaMovimentacao: '2026-05-16',
    atribuidoEm: '2026-04-05',
    contrato: {
      numero: 'PRC-2022-0998',
      clausulaArbitral: 'Cláusula 11ª',
      clausulaCompromissoria: 'Cláusula 14ª',
      valor: 'R$ 88.200,00',
    },
    pendencias: [],
    dataSentenca: null,
    prazoEmbargos: null,
  },
];

// TODO: substituir o mock de processos por chamada real quando a API do painel-home existir.
// (As notificações do árbitro já são server-driven — ver painelProcessosService.listarNotificacoes.)
export const arbitroWorkspaceService = {
  listarProcessos: async (): Promise<ProcessoArbitro[]> =>
    delay(processosSeed.map((processo) => enriquecerProcesso(processo))),
};
